import type { ButtonHTMLAttributes, InputHTMLAttributes, Ref, ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

/** Joins class names, dropping falsy entries. */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// --- Layout ---------------------------------------------------------------

export function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cx('mx-auto w-full max-w-6xl px-5 sm:px-8', className)}>{children}</div>;
}

export function Section({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    // `scroll-mt-20` only matters when the section is an anchor target. The
    // header is sticky and 4rem tall, so without it a jump to `#architecture`
    // lands the heading underneath the header and looks like it scrolled to
    // the wrong place. Applied only when there is an id, because a section
    // nobody links to should not carry a scroll offset it never uses.
    <section id={id} className={cx('py-16 sm:py-24', id && 'scroll-mt-20', className)}>
      <Container>{children}</Container>
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-surge-400">
      {children}
    </p>
  );
}

export function Heading({
  children,
  level = 2,
  className,
}: {
  children: ReactNode;
  level?: 1 | 2 | 3;
  className?: string;
}) {
  const sizes = {
    1: 'text-4xl sm:text-5xl lg:text-6xl',
    2: 'text-2xl sm:text-3xl lg:text-4xl',
    3: 'text-lg sm:text-xl',
  } as const;

  const Tag = `h${level}` as 'h1' | 'h2' | 'h3';
  return <Tag className={cx(sizes[level], 'font-semibold', className)}>{children}</Tag>;
}

export function Lead({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx('text-base leading-relaxed text-hull-300 sm:text-lg', className)}>{children}</p>;
}

export function Card({
  children,
  className,
  id,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  /** Set when the card is a link target, so `#anchor` navigation reaches it. */
  id?: string;
  as?: 'div' | 'li' | 'article';
}) {
  return (
    <Tag
      id={id}
      className={cx(
        'rounded-card border border-hull-700 bg-hull-800/60 p-6 backdrop-blur',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

// --- Actions --------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-surge-500 text-hull-950 hover:bg-surge-400',
  secondary: 'border border-hull-600 bg-hull-800 text-white hover:border-surge-400',
  ghost: 'text-hull-200 hover:text-white',
};

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold ' +
  'transition disabled:cursor-not-allowed disabled:opacity-50';

export function Button({
  variant = 'primary',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return <button className={cx(BASE, VARIANTS[variant], className)} {...props} />;
}

export function ButtonLink({
  to,
  variant = 'primary',
  className,
  children,
  download = false,
}: {
  to: string;
  variant?: Variant;
  className?: string;
  children: ReactNode;
  /**
   * Set for same-origin *files* rather than routes.
   *
   * Without it a path like `/releases/agent.zip` renders a router `<Link>`,
   * which preventDefaults the click and pushes the path into the SPA. There is
   * no such route, so the catch-all renders the 404 page and the file is never
   * fetched. `to` not starting with `http` means "same origin", not "routable".
   */
  download?: boolean;
}) {
  if (download) {
    return (
      <a href={to} download className={cx(BASE, VARIANTS[variant], className)}>
        {children}
      </a>
    );
  }

  const external = to.startsWith('http');

  if (external) {
    return (
      <a
        href={to}
        className={cx(BASE, VARIANTS[variant], className)}
        target="_blank"
        // noopener stops the opened page reaching back via window.opener.
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={cx(BASE, VARIANTS[variant], className)}>
      {children}
    </Link>
  );
}

// --- Forms ----------------------------------------------------------------

export function Field({
  label,
  hint,
  errors,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  errors?: string[];
  children: ReactNode;
  htmlFor: string;
}) {
  const errorId = errors?.length ? `${htmlFor}-error` : undefined;
  const hintId = hint ? `${htmlFor}-hint` : undefined;

  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-hull-200">
        {label}
      </label>
      {children}
      {hint && (
        <p id={hintId} className="mt-1.5 text-xs text-hull-400">
          {hint}
        </p>
      )}
      {errors?.length ? (
        // aria-live so a screen reader announces validation that appears after
        // submit, rather than leaving it silent (WCAG 4.1.3).
        <ul id={errorId} className="mt-1.5 space-y-0.5" aria-live="polite">
          {errors.map((error) => (
            <li key={error} className="text-xs text-fail-400">
              {error}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// React 19 passes `ref` as an ordinary prop to function components, so no
// forwardRef wrapper is needed - only the type has to say so.
export function Input({
  className,
  invalid,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}) {
  return (
    <input
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${props.id}-error` : undefined}
      className={cx(
        'w-full rounded-lg border bg-hull-950 px-3.5 py-2.5 text-sm text-white',
        'placeholder:text-hull-500',
        invalid ? 'border-fail-400' : 'border-hull-600',
        className,
      )}
      {...props}
    />
  );
}

export function Alert({
  tone,
  children,
}: {
  tone: 'success' | 'error' | 'warn' | 'info';
  children: ReactNode;
}) {
  const tones = {
    success: 'border-pass-600/40 bg-pass-600/10 text-pass-400',
    error: 'border-fail-600/40 bg-fail-600/10 text-fail-400',
    // Something the reader has to act on, but nothing has gone wrong and
    // nothing is broken — a promotional period that has run out, say. Reaching
    // for `error` there would put a red alert in front of a customer whose
    // account is working exactly as sold.
    warn: 'border-warn-600/40 bg-warn-600/10 text-warn-400',
    info: 'border-hull-600 bg-hull-800 text-hull-200',
  } as const;

  return (
    <div
      // Errors interrupt; confirmations wait their turn.
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('mb-4 rounded-lg border px-4 py-3 text-sm', tones[tone])}
    >
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-hull-400">
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-hull-600 border-t-surge-400"
      />
      {label}
    </span>
  );
}

/**
 * Icon-only "copy to clipboard" control.
 *
 * The icon carries no visible text, so `label` is what a screen reader
 * announces - without it the button reads as "button" and is unusable. It
 * names the *thing* being copied ("Copy install commands"), not just "Copy",
 * because a page with several of these otherwise offers identical buttons.
 */
export function CopyButton({
  value,
  label,
  className,
}: {
  value: string;
  label: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the text stays on screen to select.
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={copied ? `${label} - copied` : label}
      title={copied ? 'Copied' : label}
      className={cx(
        'inline-flex h-8 w-8 items-center justify-center rounded-md border transition',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'focus-visible:outline-surge-400',
        copied
          ? 'border-pass-600/40 bg-pass-600/10 text-pass-400'
          // Opaque, not translucent: the block below scrolls horizontally and
          // long command lines would otherwise show through the button.
          : 'border-hull-700 bg-hull-900 text-hull-400 hover:border-hull-500 hover:text-white',
        className,
      )}
    >
      {/* Decorative: the button's aria-label already names the action. */}
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {copied ? (
          <polyline points="20 6 9 17 4 12" />
        ) : (
          <>
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </>
        )}
      </svg>
      {/* Announced on change so the copy is confirmed without a visible label. */}
      <span role="status" className="sr-only">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </button>
  );
}

/**
 * A command block with its own copy control.
 *
 * The button sits inside the block rather than above it so that every command
 * on a page carries its own affordance, instead of one shared button whose
 * target the reader has to infer.
 */
export function CommandBlock({
  children,
  label,
  className,
}: {
  children: string;
  label: string;
  className?: string;
}) {
  return (
    <div className={cx('relative flex', className)}>
      {/*
        Wraps rather than scrolls. A horizontal scrollbar hides the tail of a
        long command behind a gesture, and side-by-side blocks then disagree in
        height; wrapping keeps every character visible and lets the boxes align.
      */}
      <pre
        className={
          'w-full whitespace-pre-wrap wrap-break-word rounded-lg border border-hull-700 ' +
          'bg-hull-950 p-4 pr-14 font-mono text-xs leading-relaxed text-hull-200'
        }
      >
        <code>{children}</code>
      </pre>
      <div className="absolute right-2 top-2">
        <CopyButton value={children} label={label} />
      </div>
    </div>
  );
}

/**
 * A collapsible section, built on `<details>` rather than on state.
 *
 * The security documentation lists a few hundred controls. Rendering them all
 * open makes the page unreadable and rendering them in a bespoke accordion
 * loses the two things that matter most on a reference page: browser find-in-
 * page reaches text inside a `<details>`, and a keyboard user gets open/close
 * on Enter without any code. Neither survives a div-based accordion.
 */
export function Disclosure({
  summary,
  count,
  children,
  className,
}: {
  summary: string;
  /** Shown beside the title — how many items are inside, before opening it. */
  count?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <details
      className={cx(
        'group rounded-card border border-hull-700 bg-hull-800/60 backdrop-blur',
        className,
      )}
    >
      <summary
        className={
          'flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 ' +
          'text-base font-semibold text-white ' +
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
          'focus-visible:outline-surge-400'
        }
      >
        <span>{summary}</span>
        <span className="flex shrink-0 items-center gap-3">
          {count && <span className="text-xs font-normal text-hull-400">{count}</span>}
          {/* Decorative: the summary element already announces expanded state. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="text-hull-400 transition-transform group-open:rotate-180"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </summary>
      <div className="border-t border-hull-700 px-6 py-5">{children}</div>
    </details>
  );
}

/**
 * A horizontally scrollable table wrapper.
 *
 * The control tables are wider than a phone. Without this the whole page
 * scrolls sideways, which moves the navigation off screen.
 */
export function ScrollTable({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0">
      <table className="w-full min-w-xl border-collapse text-left text-sm">
        {children}
      </table>
    </div>
  );
}
